<script lang="ts">
	//Hooks
	import { onMount } from 'svelte';
	//Formating
	import { format } from '../../../../services/i18n';
	//Services
	import { ChunkRequest, gen2Request } from '../../../../services/gen_2_http_reqs';
	//Helpers
	import { real_id } from '../../../../helpers/interface_helpers';
	import {
		capFirstChar,
		formatSnakeCaseToTitle,
		removeOrAddUnderscore,
		sortOn,
		transformedTimeZones,
	} from '../../../../helpers/utilities';
	import { onSettingsResponse, sleepingResponse } from '../../../../helpers/device_settings_helpers';
	import { getTimeZoneName, validateGeolocation } from '../../../../helpers/geolocalion_helper';
	import { getNumberInputConfig } from '../../../../helpers/number_input_helpers';
	import { coordinatesValidationHelper } from '../../../../helpers/coordinates_helpers';
	//Stores
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Maps
	import SelectDeviceLocation from '../../../Maps/SelectDeviceLocation.svelte';
	import DeviceLocationPreview from '../../../Maps/DeviceLocationPreview.svelte';
	//Design system components
	import {
		Alert,
		ButtonV2,
		InputV2,
		Loader,
		SelectV2,
		Text,
		toast,
	} from '@Shelly/shelly-design-system';
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	import type { Device } from '../../../../types/Device';
	import SettingGuide from '../../SettingGuide.svelte';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';

	export let device: Device;

	const id = real_id(device.id);
	const settings = devices_settings.getByRealId(id);
	const loading = { detect: false, save: false };
	let timeZones = null;
	let timeZone = settings.sys.location?.tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
	let longitude = settings.sys.location?.lon;
	let latitude = settings.sys.location?.lat;
	let timeZonesListError: string = null;
	let reqResTimezones: string[] = [];
	let chunkRequest = new ChunkRequest(device, 'Shelly.ListTimeZones', {}, false);
	let waitForResponse = false;
	let showModal = false;

	onMount(() => {
		if (!validateGeolocation(latitude, longitude)) {
			latitude = 0;
			longitude = 0;
		}
		getTimeZones();
		return () => {
			chunkRequest.abort();
		};
	});

	const getTimeZones = async () => {
		waitForResponse = true;
		timeZonesListError = null;
		reqResTimezones = [];

		try {
			const res: SettingRequest = await chunkRequest.get();

			if (chunkRequest.isAborted()) {
				return;
			}

			if (res?.isok) {
				reqResTimezones = res.data?.timezones || [];
				timeZones = transformedTimeZones(reqResTimezones);

				const isInList = timeZones.some((el) => el.stringToSend === timeZone);

				if (isInList) {
					timeZone = timeZones.find((el) => el.stringToSend === timeZone)?.stringToVisualise;
				} else {
					timeZones.push({
						stringToVisualise: removeOrAddUnderscore(timeZone, 'removeUnderscore'),
						stringToSend: removeOrAddUnderscore(timeZone, 'addUnderscore'),
					});

					timeZones = sortOn(timeZones, 'stringToSend');
					timeZone = timeZones.find((el) => el.stringToSend === timeZone)?.stringToVisualise;
				}
			}
			if (!timeZones) {
				timeZonesListError =
					sleepingResponse(res, device.type) ||
					$format('device_timezones_error', {
						default: 'Cloud not get device timezones.',
					});
			}
			waitForResponse = false;
		} catch (error) {
			console.error('error on getting timezones list', error);
			timeZonesListError =
				$format('device_timezones_error', {
					default: 'Cloud not get device timezones.',
				});
			waitForResponse = false;
		}
	};

	const saveLocationSettings = async () => {
		if (
			latitude >= 90 &&
			latitude.toString().includes('.') &&
			latitude.toString().split('.')[1].startsWith('00000')
		) {
			latitude = +latitude.toString().split('.')[0];
		}

		if (
			longitude >= 180 &&
			longitude.toString().includes('.') &&
			longitude.toString().split('.')[1].startsWith('00000')
		) {
			longitude = +longitude.toString().split('.')[0];
		}

		if (!validateGeolocation(latitude, longitude)) {
			toast.open({
				type: 'danger',
				title: $format('invalid_arguments', { default: 'Invalid arguments' }),
				text: $format('text_invalid_arguments', {
					default:
						"'Latitude' should be greater than -90 and less than 90, 'longitude' should be greater than -180 and less than 180!",
				}),
			});
			return;
		}

		loading.save = true;

		const tz = timeZones.find((el) => el.stringToVisualise === timeZone)?.stringToSend;

		const params = {
				config: {
					location: {
						tz,
						lat: Number(latitude),
						lon: Number(longitude),
					},
				},
			},
			res: SettingRequest = await gen2Request(device, 'Sys.SetConfig', params);
		if (res.isok) {
			settings.sys.location.tz = tz;
			settings.sys.location.lon = longitude;
			settings.sys.location.lat = latitude;
			devices_settings.update_devices_settings(id, settings);
		}

		onSettingsResponse(res, device, {
			success: $format('location_save_scc_toast', {
				default: 'Location settings saved successfully.',
			}),
			danger: $format('location_save_err_toast', {
				default: 'Failed to save location settings!',
			}),
		});

		loading.save = false;
	};

	const detectLocationSettings = async () => {
		loading.detect = true;
		const res: SettingRequest = await gen2Request(device, 'Shelly.DetectLocation', {}, false);

		if (res.isok) {
			const data = res.data || res;
			const rawTz = data['tz'];
			timeZone =
				timeZones.find((el) => el.stringToSend === rawTz)?.stringToVisualise ??
				removeOrAddUnderscore(rawTz, 'removeUnderscore');
			longitude = data['lon'];
			latitude = data['lat'];
			settings.sys.location.tz = rawTz;
			settings.sys.location.lat = latitude;
			settings.sys.location.lon = longitude;
			devices_settings.update_devices_settings(id, settings);
			toast.open({
				type: 'success',
				title: $format('location_detect_scc_toast', {
					default: 'Time zone auto detect completed successfully.',
				}),
			});
		} else {
			toast.open({
				type: 'danger',
				title: $format('location_detect_err_toast', {
					default: 'Failed to auto detect time zone!',
				}),
			});
		}
		loading.detect = false;
	};

	const onSelectMapLocation = async (e: CustomEvent) => {
		latitude = Number(e.detail.lat.toFixed(6));
		longitude = Number(e.detail.lng.toFixed(6));
		const newTimeZone = await getTimeZoneName(e.detail.lat, e.detail.lng);
		if (reqResTimezones.includes(newTimeZone)) {
			timeZone =
				timeZones.find((el) => el.stringToSend === newTimeZone)?.stringToVisualise ??
				removeOrAddUnderscore(newTimeZone, 'removeUnderscore');
		}
	};

	const openMapModal = () => {
		if (
			latitude >= 90 &&
			latitude.toString().includes('.') &&
			latitude.toString().split('.')[1].startsWith('00000')
		) {
			latitude = +latitude.toString().split('.')[0];
		}

		if (
			longitude >= 180 &&
			longitude.toString().includes('.') &&
			longitude.toString().split('.')[1].startsWith('00000')
		) {
			longitude = +longitude.toString().split('.')[0];
		}

		if (!validateGeolocation(latitude, longitude)) {
			latitude = settings.sys.location?.lat;
			longitude = settings.sys.location?.lon;
		}
		showModal = true;
	};

	$: {
		if (latitude) latitude = coordinatesValidationHelper(latitude.toString());
		if (longitude) longitude = coordinatesValidationHelper(longitude.toString());
	}

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if (timeZones !== null && !waitForResponse && !timeZonesListError) {
			setStickyBottomAction({
				onClick: saveLocationSettings,
				loading: loading.save,
				disabled: loading.detect || loading.save,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	}
</script>

{#if timeZonesListError}
	<Alert type="danger" heading={timeZonesListError} class="mb-s" />
	<ButtonV2 size="m" width="full" variant="primary" on:click={getTimeZones}>
		{$format('button_rescan', { default: 'Rescan' })}
	</ButtonV2>
{:else if timeZones === null || waitForResponse}
	<div class="geo-loading">
		<Loader size="l" />
		<Text styleType="body-small" class="text-color-muted mt-s text-center">
			{$format('settings_loading_location_and_time_zone', {
				default: 'Fetching location and time zone from the device…',
			})}
		</Text>
	</div>
{:else}
	<div class="location-g2-settings">
		<SettingGuide
			text={$format('settings_guide_location_and_time', {
				default:
					'Used for schedules, sunrise and sunset, and energy reports. Set this device’s location and time zone, or let it detect them automatically.',
			})}
		/>

		<DeviceLocationPreview
			lat={Number(latitude)}
			lng={Number(longitude)}
			disabled={loading.save || loading.detect}
			on:open={openMapModal}
		/>

		<ButtonV2
			size="m"
			loading={loading.detect}
			width="full"
			variant="secondary"
			disabled={loading.save}
			on:click={detectLocationSettings}
		>
			{capFirstChar(
				$format('button_location_auto_detect', {
					default: 'auto detect location',
				}),
			)}
		</ButtonV2>
		{#if showModal}
			<SelectDeviceLocation
				bind:showModal
				lat={latitude}
				lng={longitude}
				on:change={onSelectMapLocation}
			/>
		{/if}

		<SelectV2
			size="m"
			width="full"
			label={formatSnakeCaseToTitle($format('location_time_zone', { default: 'time zone' }))}
			bind:value={timeZone}
			isAutocomplete
			icon="fas fa-globe"
			options={timeZones.map((t) => t.stringToVisualise)}
			id="select-4ss5ruf"
			aria-label={$format('location_time_zone', { default: 'time zone' })}
		/>

		<InputV2
			{...getNumberInputConfig()}
			size="m"
			label={formatSnakeCaseToTitle($format('location_latitude', { default: 'latitude' }))}
			bind:value={latitude}
			min={-90}
			max={90}
			step={0.000001}
			id="input-pjci61n"
			aria-label={$format('location_latitude', { default: 'latitude' })}
		/>

		<InputV2
			{...getNumberInputConfig()}
			size="m"
			label={formatSnakeCaseToTitle($format('location_longtitude', { default: 'longitude' }))}
			bind:value={longitude}
			min={-180}
			max={180}
			step={0.000001}
			id="input-jfha90p"
			aria-label={$format('location_longtitude', { default: 'longitude' })}
		/>
	</div>
{/if}

<style>
	.geo-loading {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		min-height: 240px;
		width: 100%;
	}
	.location-g2-settings {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 12px;
		width: 100%;
	}
</style>
